import { Component, PLATFORM_ID, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';

import { MessagingRulesTab } from './messaging-rules';
import { MessageTemplatesPage } from '../message-templates/message-templates';
import { CommunicationConfigPage } from '../communication-config/communication-config';
import { Notifications } from '../notifications/notifications';
import { PageTitleService } from '../../services/page-title';

type TabKey = 'rules' | 'templates' | 'providers' | 'outbox';
const TAB_KEYS: TabKey[] = ['rules', 'templates', 'providers', 'outbox'];

/**
 * The single place messaging is managed. Absorbs what used to be five separate menu entries
 * (partner toggles, client toggles, templates, providers, outbox) — the old routes redirect
 * here with ?tab=, so existing bookmarks keep working.
 *
 * Panels are rendered lazily (@if on the active tab) so opening this page doesn't fire four
 * screens' worth of API calls.
 */
@Component({
  selector: 'app-messaging',
  standalone: true,
  imports: [
    CommonModule, TabsModule,
    MessagingRulesTab, MessageTemplatesPage, CommunicationConfigPage, Notifications,
  ],
  templateUrl: './messaging.html',
})
export class MessagingPage {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  readonly pageTitle = inject(PageTitleService).title;
  activeTab = signal<TabKey>('rules');

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    const tab = this.route.snapshot.queryParamMap.get('tab') as TabKey | null;
    if (tab && TAB_KEYS.includes(tab)) this.activeTab.set(tab);
  }

  onTabChange(value: any) {
    const tab = (TAB_KEYS.includes(value) ? value : 'rules') as TabKey;
    this.activeTab.set(tab);
    // Reflect the tab in the URL so a refresh (and the redirects from the old routes) restore it.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
